import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bell, Gauge, Radio, TrendingDown } from "lucide-react";
import { Navbar } from "../components/sections/Navbar";
import { Footer } from "../components/sections/Footer";
import { ScrollProgress } from "../components/visuals/ScrollProgress";
import useReveal from "../lib/useReveal";

// Uses the exact reference oil-light image the user provided,
// masked so the color adapts to context (white on red, red on white, etc.).
const OIL_ICON_URL =
    "https://customer-assets-m6fa6gv7.emergentagent.net/job_narcan-dispenser-pro/artifacts/4gkxyy42_image.png";

const OilLightIcon = ({ className = "", size = 64 }) => (
    <span
        aria-hidden="true"
        className={className}
        style={{
            display: "block",
            width: size,
            height: size * (60 / 120),
            backgroundColor: "currentColor",
            WebkitMaskImage: `url(${OIL_ICON_URL})`,
            maskImage: `url(${OIL_ICON_URL})`,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            maskPosition: "center",
            WebkitMaskSize: "contain",
            maskSize: "contain",
        }}
    />
);

// A little chain-link so the keychain section reads immediately
const KeychainAccent = ({ className = "" }) => (
    <svg viewBox="0 0 40 40" className={className} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="12" cy="20" r="6" />
        <circle cx="28" cy="20" r="6" />
        <line x1="18" y1="20" x2="22" y2="20" />
    </svg>
);

const STEPS = [
    { icon: Gauge, label: "Oil gets used" },
    { icon: TrendingDown, label: "Weight drops" },
    { icon: Radio, label: "Kairos detects it" },
    { icon: Bell, label: "Your team gets alerted" },
    { icon: OilLightIcon, label: "Reorder before you run out" },
];

export default function AutoShopsPage() {
    useReveal();
    return (
        <main data-testid="auto-shops-page" className="min-h-screen bg-white">
            <ScrollProgress />
            <Navbar />

            {/* HERO */}
            <section className="relative bg-white pt-28 md:pt-40 pb-16 md:pb-24 overflow-hidden">
                <div className="max-w-6xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14 items-center">
                        <div className="lg:col-span-7 reveal">
                            <div className="inline-flex items-center gap-2.5 mb-8">
                                <span className="relative inline-flex h-2 w-2">
                                    <span className="absolute inline-flex h-full w-full rounded-full bg-[#F0493E] pulse-ring" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#F0493E]" />
                                </span>
                                <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                                    For Auto Shops
                                </span>
                            </div>

                            <h1 className="font-display font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-[76px] leading-[0.95] tracking-tighter text-[#0A1428] text-balance">
                                Your car warns you
                                <br />
                                about low oil.
                                <br />
                                <span className="text-[#F0493E]">Now your shop can too.</span>
                            </h1>

                            <p className="mt-8 max-w-xl text-lg md:text-xl text-slate-600 leading-relaxed font-medium">
                                Kairos monitors your bulk oil supply by
                                weight and alerts your team when inventory
                                gets low — so you can reorder before running
                                out.
                            </p>

                            <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:items-center">
                                <Link
                                    to="/contact?interest=alert-automation&device=Bulk%20Oil%20Monitoring"
                                    data-testid="auto-hero-cta"
                                    className="btn-primary inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm"
                                >
                                    Request a free shop assessment
                                    <ArrowRight size={18} strokeWidth={2} />
                                </Link>
                                <a
                                    href="#how-it-works"
                                    className="text-[11px] font-bold uppercase tracking-[0.28em] text-slate-500 hover:text-[#0A1428] transition-colors self-center"
                                >
                                    See how it works ↓
                                </a>
                            </div>
                        </div>

                        {/* Hero visual — oil drum + weight sensor + live level */}
                        <div className="lg:col-span-5 reveal">
                            <HeroVisual />
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section
                id="how-it-works"
                data-testid="auto-how-it-works"
                className="relative bg-slate-50 py-20 md:py-28 border-y border-slate-200 scroll-mt-24"
            >
                <div className="max-w-6xl mx-auto px-6 md:px-12">
                    <div className="reveal max-w-xl">
                        <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            How it works
                        </span>
                        <h2 className="mt-6 font-display font-extrabold text-3xl md:text-5xl leading-[1] tracking-tighter text-[#0A1428] text-balance">
                            Simple as
                            <br />
                            watching a gauge.
                        </h2>
                    </div>

                    <ol className="reveal mt-14 space-y-3">
                        {STEPS.map((s, i) => (
                            <li
                                key={s.label}
                                data-testid={`auto-step-${i}`}
                                className="flex items-center gap-4 md:gap-6 bg-white border border-slate-200 p-4 md:p-6"
                            >
                                <div className="shrink-0 h-10 w-10 md:h-12 md:w-12 grid place-items-center border border-[#0A1428] font-display font-extrabold text-[#0A1428] text-sm md:text-base">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                <div className="flex-1 font-display font-bold text-lg md:text-2xl text-[#0A1428] tracking-tight leading-tight">
                                    {s.label}
                                </div>
                                <div className="shrink-0 h-10 w-10 md:h-12 md:w-12 grid place-items-center">
                                    <s.icon size={22} strokeWidth={1.5} className="text-[#F0493E]" />
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* PAIN POINT */}
            <section className="relative bg-white py-20 md:py-28">
                <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
                    <div className="reveal">
                        <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            The Problem
                        </span>
                        <h2 className="mt-6 font-display font-extrabold text-3xl md:text-5xl lg:text-6xl leading-[1] tracking-tighter text-[#0A1428] text-balance">
                            Running out shouldn&apos;t
                            <br />
                            be how you find out
                            <br />
                            <span className="text-[#F0493E]">you&apos;re low.</span>
                        </h2>
                        <p className="mt-8 max-w-2xl mx-auto text-lg md:text-xl text-slate-600 leading-relaxed font-medium">
                            Manual checks get forgotten. Deliveries take
                            time. Kairos continuously monitors your oil
                            supply so your team knows when it&apos;s time to
                            reorder.
                        </p>
                    </div>
                </div>
            </section>

            {/* KEYCHAIN */}
            <section
                data-testid="auto-keychain-section"
                className="relative bg-[#0A1428] text-white py-24 md:py-32 overflow-hidden"
            >
                <div className="absolute inset-0 dot-grid-light opacity-25 pointer-events-none" />
                <div className="absolute -top-24 -right-24 w-[480px] h-[480px] opacity-[0.06] pointer-events-none float-slow">
                    <svg viewBox="0 0 200 200">
                        <polygon points="20,160 130,60 180,160" fill="#F0493E" />
                    </svg>
                </div>

                <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
                    <div className="reveal">
                        {/* Keychain: oil light */}
                        <div className="mx-auto mb-10 flex items-center justify-center">
                            <div className="relative h-28 w-28 md:h-36 md:w-36 grid place-items-center bg-[#F0493E] rounded-full shadow-[0_20px_60px_-15px_rgba(240,73,62,0.55)]">
                                <span className="absolute inset-0 rounded-full pulse-ring" />
                                <OilLightIcon className="text-white relative" size={128} />
                            </div>
                        </div>

                        <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            About Your Keychain
                        </span>
                        <h2 className="mt-6 font-display font-extrabold text-3xl md:text-5xl leading-[1] tracking-tighter text-balance">
                            Why did we give you
                            <br />
                            an oil light?
                        </h2>
                        <p className="mt-8 max-w-2xl mx-auto text-base md:text-lg text-slate-300 leading-relaxed font-medium">
                            Because that little warning light has one job —
                            tell you about a problem before it becomes a
                            bigger one.
                        </p>

                        <div className="mt-14 md:mt-16 pt-10 border-t border-white/10">
                            <h3 className="font-display font-extrabold text-3xl md:text-5xl lg:text-6xl leading-[1] tracking-tighter text-balance">
                                We think your shop
                                <br />
                                <span className="text-[#F0493E]">
                                    should work the same way.
                                </span>
                            </h3>
                            <p className="mt-8 text-base md:text-lg text-slate-300 font-medium">
                                Kairos brings that same idea to your oil
                                supply.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section
                data-testid="auto-cta-section"
                className="relative bg-white py-24 md:py-32"
            >
                <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
                    <div className="reveal">
                        <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            Get Started
                        </span>
                        <h2 className="mt-6 font-display font-extrabold text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tighter text-[#0A1428] text-balance">
                            Stop checking.
                            <br />
                            <span className="text-[#F0493E]">Start getting alerts.</span>
                        </h2>
                        <p className="mt-8 max-w-xl mx-auto text-base md:text-lg text-slate-600 leading-relaxed font-medium">
                            We&apos;ll look at your current oil storage and
                            show you where automated monitoring could fit.
                        </p>
                        <Link
                            to="/contact?interest=alert-automation&device=Bulk%20Oil%20Monitoring%20(Auto%20Shop)"
                            data-testid="auto-final-cta"
                            className="btn-primary mt-10 inline-flex items-center justify-center gap-3 px-8 py-5 md:py-5 text-base md:text-sm font-bold tracking-wide uppercase rounded-sm w-full sm:w-auto"
                        >
                            Request a free shop assessment
                            <ArrowRight size={18} strokeWidth={2} />
                        </Link>
                        <div className="mt-8 text-[10px] font-bold uppercase tracking-[0.32em] text-slate-500">
                            Built for the Moment That Matters
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}

// Hero visual — a stylized oil drum with a live weight readout.
// Animates the level slowly downward so mobile visitors immediately
// grasp "supply is being watched."
const HeroVisual = () => {
    const [pct, setPct] = useState(72);
    useEffect(() => {
        let t;
        const tick = () => {
            setPct((p) => {
                if (p <= 22) return 72; // loop
                return p - 0.35;
            });
            t = setTimeout(tick, 60);
        };
        t = setTimeout(tick, 400);
        return () => clearTimeout(t);
    }, []);

    const low = pct <= 30;

    return (
        <div className="relative">
            {/* Card frame */}
            <div className="relative bg-slate-50 border border-slate-200 p-6 md:p-8">
                {/* Drum */}
                <div className="mx-auto relative w-40 sm:w-48 md:w-56 h-56 sm:h-64 md:h-72">
                    {/* body */}
                    <div className="absolute inset-x-0 top-4 bottom-0 bg-white border-2 border-[#0A1428] rounded-md overflow-hidden">
                        {/* level fill */}
                        <div
                            className="absolute inset-x-0 bottom-0 transition-[height,background-color] duration-300 ease-linear"
                            style={{
                                height: `${pct}%`,
                                background: low
                                    ? "linear-gradient(180deg,#F0493E, #B32E24)"
                                    : "linear-gradient(180deg,#1A2E52, #0A1428)",
                            }}
                        />
                        {/* horizontal ridges (drum banding) */}
                        <div className="absolute inset-0 pointer-events-none">
                            <div className="absolute inset-x-0 top-[30%] h-px bg-black/10" />
                            <div className="absolute inset-x-0 top-[62%] h-px bg-black/10" />
                        </div>
                        {/* label */}
                        <div className="absolute top-3 inset-x-0 flex items-center justify-center">
                            <div className="px-2 py-0.5 bg-white border border-[#0A1428] text-[9px] font-bold uppercase tracking-[0.22em] text-[#0A1428]">
                                Bulk Oil
                            </div>
                        </div>
                    </div>
                    {/* top cap */}
                    <div className="absolute top-0 inset-x-6 h-6 bg-[#0A1428] rounded-t-md" />

                    {/* Kairos weight-sensor pad under the drum */}
                    <div className="absolute -bottom-3 inset-x-[-8%] h-4 bg-slate-800 rounded-sm shadow-[0_10px_30px_-10px_rgba(0,0,0,0.3)]" />
                </div>

                {/* Readout */}
                <div className="mt-10 grid grid-cols-2 gap-3">
                    <div className="bg-white border border-slate-200 p-4">
                        <div className="text-[9px] font-bold uppercase tracking-[0.28em] text-slate-500">
                            Level
                        </div>
                        <div
                            className={`mt-2 font-display font-extrabold text-3xl tnum tracking-tighter transition-colors ${
                                low ? "text-[#F0493E]" : "text-[#0A1428]"
                            }`}
                        >
                            {Math.round(pct)}
                            <span className="text-base ml-0.5">%</span>
                        </div>
                    </div>
                    <div className="bg-white border border-slate-200 p-4 flex items-center justify-between">
                        <div>
                            <div className="text-[9px] font-bold uppercase tracking-[0.28em] text-slate-500">
                                Status
                            </div>
                            <div
                                className={`mt-2 font-display font-extrabold text-lg tracking-tight ${
                                    low ? "text-[#F0493E]" : "text-emerald-600"
                                }`}
                            >
                                {low ? "REORDER" : "MONITORING"}
                            </div>
                        </div>
                        <span className="relative inline-flex h-2.5 w-2.5">
                            <span
                                className={`absolute inline-flex h-full w-full rounded-full pulse-ring ${
                                    low ? "bg-[#F0493E]" : "bg-emerald-500"
                                }`}
                            />
                            <span
                                className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                                    low ? "bg-[#F0493E]" : "bg-emerald-500"
                                }`}
                            />
                        </span>
                    </div>
                </div>
            </div>

            {/* KAIROS caption */}
            <div className="mt-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.28em] text-slate-500">
                <span className="h-1.5 w-1.5 rounded-full bg-[#F0493E]" />
                Kairos Weight Monitoring · Live
            </div>
        </div>
    );
};
